/**
 * The universal set of things that can be @-mentioned across every surface
 * (Arc chat, Notes, Tasks). Resolved by resolveMentionSuggestions; the subset a
 * given surface offers is governed by its mention `purpose`, not by type.
 */
export enum MentionType {
  Client = 'Client',
  InternalUser = 'InternalUser',
  ClientUser = 'ClientUser',
  Entity = 'Entity',
  BBC = 'BBC',
  OngoingReporting = 'OngoingReporting',
  DueDiligence = 'DueDiligence',
  Prospect = 'Prospect',
  Contact = 'Contact',
  // A named group of internal users (see common/interfaces/team). Unlike every
  // record kind above, a Team stands for a set of PEOPLE: AIM, Tasks and Notes
  // expand it to its members once, at tag time (channels/resolveTeamMentions,
  // tasks/teamMentionRecipients, notes/teamNoteMentions). The chip stays one chip.
  Team = 'Team',
  Opportunity = 'Opportunity',
  Company = 'Company',
  Organization = 'Organization',
  // The in-view amendment analysis (general_analyses row). Surfaced as a chat
  // chip from the Amendment Analysis page so Arc targets the right amendment
  // for commentary edits.
  AmendmentAnalysis = 'AmendmentAnalysis',
  // Broadcast tokens, valid only in channel messages (see BROADCAST_MENTION_OPTIONS).
  // Unlike every other kind above, these don't name a single record — they stand for
  // a group of recipients, expanded into concrete ids once, before the message row is
  // written (see channels/resolveBroadcastMentions), rather than looked up by each
  // reader afterwards.
  Here = 'Here',
  Channel = 'Channel',
}

/**
 * What the resolved mention is FOR. Governs which kinds a surface is offered:
 *  - 'context' — the mention feeds data into the AI (Arc).
 *  - 'link'    — the mention is a navigable chip (e.g. CRM mention review).
 *  - 'note'    — restricted to the kinds a note can persist (Teams included).
 *  - 'tag'     — 'link', plus Teams. For AIM and Atlas Tasks.
 * Every surface offered Teams expands a team mention to its members at tag time.
 *
 * Shared rather than server-only because the client picks the value and has to
 * name the same set — it used to be restated as an inline union in a dozen
 * components, which is how a new value would have been missed by half of them.
 */
export type MentionPurpose = 'context' | 'link' | 'note' | 'tag' | 'people'

/** The two broadcast tokens, as a type — see MentionType.Here/.Channel. */
export type BroadcastMentionType = MentionType.Here | MentionType.Channel

/** Iterable form of BroadcastMentionType, for code that has to consider both. */
export const BROADCAST_MENTION_TYPES: BroadcastMentionType[] = [
  MentionType.Here,
  MentionType.Channel,
]

/**
 * A reserved `@here`/`@channel` token, offered by the channel-message composer
 * alongside individual mentions. `id`/`name` are fixed sentinels (not a real
 * record id) — `channels/resolveBroadcastMentions` is what turns one into an
 * actual recipient set, reading channel membership/presence once at send time
 * and persisting the answer alongside the token (see IAtlasAIMention.broadcast),
 * so every later reader sees the audience the message was actually sent to.
 */
export interface IBroadcastMentionOption {
  id: 'here' | 'channel'
  name: 'here' | 'channel'
  type: BroadcastMentionType
  description: string
}

// Named and described the way Slack names them, because that is the vocabulary
// people arrive with — `@channel` for everyone, `@here` for whoever is around.
// "@channel" was our own coinage and would have been one more thing to learn.
export const BROADCAST_MENTION_OPTIONS: IBroadcastMentionOption[] = [
  {
    id: 'here',
    name: 'here',
    type: MentionType.Here,
    description: 'Notify only active members of this channel',
  },
  {
    id: 'channel',
    name: 'channel',
    type: MentionType.Channel,
    description: 'Notify everyone in this channel',
  },
]

// The kinds a note can persist (NoteMentions models user/client/entity, and a
// team as one user row per member). Used by note validation + the 'note' mention
// purpose to avoid suggesting un-saveable types.
export const NOTE_PERSISTABLE_MENTION_TYPES = [
  MentionType.InternalUser,
  MentionType.ClientUser,
  MentionType.Client,
  MentionType.Entity,
  MentionType.Team,
] as const

/**
 * True when a message @-mentions Arc.
 *
 * Checks the structured mention list rather than scanning the text, so writing
 * the literal word "arc" in a sentence never triggers a run — only an actual
 * mention chip resolving to the bot user does.
 *
 * Note that a chip is no longer only ever made by the picker: a channel message
 * addressing `@arc` in plain text has that resolved into a real mention before it
 * is stored (see channels/resolveTextMentions), so a typed mention reaches this
 * predicate as a structured one. The rule above is unchanged and is what makes
 * that safe — the `@` sigil is required, so the bare word stays inert.
 *
 * Shared rather than server-only because the client asks the same question for a
 * different reason: the server uses it to decide whether to dispatch a run, and
 * the timeline uses it to decide whether a message is one that Retry can re-ask.
 * Those two answers have to agree, or Retry would offer to re-run something that
 * never ran.
 */
export const mentionsArc = (
  mentions: Array<{ id: string; type?: MentionType }>,
  arcUserId: string | null,
): boolean => !!arcUserId && mentions.some((mention) => mention.id === arcUserId)
